import React, { useContext, useEffect, useState } from 'react';
import axiosInstance from '../../AxiosInstance';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faWrench, faEnvelope, faCommentSms, faGlobe, faServer, faTimes, faFilePdf, faFile, faDownload, faImage } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../components/modal/modal';
import { TextInput } from '../../components/input/inputs';
import RecordFormModal from './components/record_form_modal';
import PhoneInput from 'react-phone-input-2';
import { PlaceholderTextarea, PlaceholderInput } from '../../components/input/PlaceholderTextarea';
import {useTranslation} from "react-i18next";
import MyContext from "../../Context";
import PaginationControls, {getStoredPageSize} from "../../components/pagination/PaginationControls";

const PLAN_PLACEHOLDERS = ['{machine_name}', '{plan_name}', '{hours_due}', '{hours_remaining}'];

function normalizePlaceholders(text) {
    let result = text;
    let prev;
    do {
        prev = result;
        result = result
            .replace(/"(\{[^{}]+\})"/g, '"__x__"')
            .replace(/\{[^{}]+\}/g, '"__x__"');
    } while (result !== prev);
    return result;
}

function tryParseJson(text) {
    if (!text || !text.trim()) return null;
    try {
        JSON.parse(normalizePlaceholders(text));
        return null;
    } catch (e) {
        return e.message;
    }
}

const jsonLabelStyle = { fontSize: '12px', fontWeight: '600', color: '#64748B', marginBottom: '4px', display: 'block' };
const jsonInputStyle = { width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none', color: '#334155', boxSizing: 'border-box' };

function JsonTextarea({ label, value, onChange, placeholder, placeholders }) {
    const {t} = useTranslation();
    const error = tryParseJson(value);
    const formatJson = () => {
        if (!value || !value.trim()) return;
        const tokens = {};
        let i = 0;
        let tokenized = value;
        let prev;
        do {
            prev = tokenized;
            tokenized = tokenized
                .replace(/"(\{[^{}]+\})"/g, (match) => {
                    const k = `__PH${i++}__`;
                    tokens[`"${k}"`] = match;
                    return `"${k}"`;
                })
                .replace(/\{[^{}]+\}/g, m => {
                    const k = `"__PH${i++}__"`;
                    tokens[k] = m;
                    return k;
                });
        } while (tokenized !== prev);
        try {
            const pretty = JSON.stringify(JSON.parse(tokenized), null, 2);
            const restored = pretty.replace(/"__PH\d+__"/g, m => tokens[m] || m);
            onChange(restored);
        } catch (_) {}
    };
    return (
        <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={jsonLabelStyle}>{label}</span>
                <button
                    type="button" onClick={formatJson}
                    disabled={!value || !!error}
                    style={{
                        fontSize: '11px', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer',
                        border: '1px solid #C7D2FE', background: '#EEF2FF', color: '#4F46E5',
                        fontWeight: 600, opacity: (!value || !!error) ? 0.4 : 1,
                    }}
                >
                    {t('gps.rules.formatJson')}
                </button>
            </div>
            <PlaceholderTextarea
                value={value || ''}
                onChange={onChange}
                placeholder={placeholder}
                placeholders={placeholders}
                style={{
                    ...jsonInputStyle,
                    fontFamily: 'monospace', fontSize: '12px',
                    minHeight: '60px', resize: 'vertical', marginBottom: error ? '2px' : '0',
                    border: `1px solid ${error ? '#EF4444' : '#E2E8F0'}`,
                    backgroundColor: error ? '#FFF8F8' : '#fff',
                }}
            />
            {error && (
                <div style={{ fontSize: '11px', color: '#EF4444', marginBottom: '6px' }}>
                    ⚠ Invalid JSON: {error}
                </div>
            )}
        </div>
    );
}

const NOTIFY_TYPE_OPTIONS = [
    { id: 1, name: 'Email' },
    { id: 3, name: 'SMS' },
    { id: 4, name: 'WhatsApp' },
    { id: 5, name: 'MQTT' },
    { id: 6, name: 'API' },
];

function detectNotifyType(plan) {
    if (plan.notify_email) return 1;
    if (plan.notify_sms) return 3;
    if (plan.notify_whatsapp) return 4;
    if (plan.notify_mqtt_connection) return 5;
    if (plan.notify_api_connection) return 6;
    return 1;
}

function planHasNotification(plan) {
    return !!(plan.notify_email || plan.notify_sms || plan.notify_whatsapp || plan.notify_mqtt_connection || plan.notify_api_connection);
}

const inputStyle = { width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '13px' };
const labelStyle = { fontSize: '13px', color: '#64748B', display: 'block', marginBottom: '4px', marginTop: '12px' };

function StatusBadge({ totalHours, nextDueHours, alertBeforeHours }) {
    const {t} = useTranslation();
    const remaining = nextDueHours - totalHours;
    if (remaining <= 0) {
        return <span style={{ padding: '3px 10px', borderRadius: '12px', background: '#FEF2F2', color: '#DC2626', fontSize: '12px', fontWeight: 600 }}>{t('maintenance.plans.overdue')}</span>;
    }
    if (remaining <= alertBeforeHours) {
        return <span style={{ padding: '3px 10px', borderRadius: '12px', background: '#FFFBEB', color: '#D97706', fontSize: '12px', fontWeight: 600 }}>{t('maintenance.plans.upcoming')}</span>;
    }
    return <span style={{ padding: '3px 10px', borderRadius: '12px', background: '#F0FDF4', color: '#16A34A', fontSize: '12px', fontWeight: 600 }}>{t('maintenance.plans.ok')}</span>;
}

function ProgressBar({ current, target }) {
    const pct = Math.min(100, Math.round((current / target) * 100));
    const color = pct >= 100 ? '#DC2626' : pct >= 80 ? '#D97706' : '#4F46E5';
    return (
        <div style={{ background: '#F1F5F9', borderRadius: '4px', height: '6px', marginTop: '6px', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: '4px', transition: 'width 0.3s' }} />
        </div>
    );
}

const EMPTY_FORM = {
    send_notification: false,
    notify_type: 1,
    notify_email: '',
    notify_sms: '',
    notify_whatsapp: '',
    notify_mqtt_connection: '',
    notify_mqtt_topic: '',
    notify_mqtt_payload: '',
    notify_api_connection: '',
    notify_api_endpoint: '',
    notify_api_method: 'POST',
    notify_api_body: '',
};

export default function MaintenancePlansTab({ machine }) {
    const {t} = useTranslation();
    const {systemSettings} = useContext(MyContext);
    const visibleNotifyTypes = NOTIFY_TYPE_OPTIONS.filter(o =>
        (o.id !== 3 || systemSettings?.sms_enabled) &&
        (o.id !== 4 || systemSettings?.whatsapp_enabled)
    );
    const project_id = parseInt(localStorage.getItem('project_id'));
    const [plans, setPlans] = useState([]);
    const [plansPage, setPlansPage] = useState(1);
    const [plansPageSize, setPlansPageSize] = useState(getStoredPageSize);
    const [machineHours, setMachineHours] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [editPlan, setEditPlan] = useState(null);
    const [form, setForm] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePlan, setDeletePlan] = useState(null);
    const [showRecordModal, setShowRecordModal] = useState(false);
    const [recordPlan, setRecordPlan] = useState(null);
    const [mqttConnections, setMqttConnections] = useState([]);
    const [apiConnections, setApiConnections] = useState([]);
    const [catalogParts, setCatalogParts] = useState([]);
    const [planParts, setPlanParts] = useState([]);
    const [originalPlanParts, setOriginalPlanParts] = useState([]);
    const [newPlanPart, setNewPlanPart] = useState({ name: '', brand: '', part_number: '', quantity: 1, spare_part: '' });
    const [showCreatePartModal, setShowCreatePartModal] = useState(false);
    const [createPartForm, setCreatePartForm] = useState({});
    const [createPartImageFile, setCreatePartImageFile] = useState(null);
    const [createPartDocFiles, setCreatePartDocFiles] = useState([]);
    const [emailChips, setEmailChips] = useState([]);
    const [emailInput, setEmailInput] = useState('');
    const [allMachines, setAllMachines] = useState([]);
    const [machineSearch, setMachineSearch] = useState('');
    const [selectedMachines, setSelectedMachines] = useState([]);

    const fetchAll = () => {
        axiosInstance.get(`maintenance_plan/?project_id=${project_id}`).then(res => { setPlans(res.data); setPlansPage(1); });
        axiosInstance.get(`machine_hours_config/?machine_id=${machine.id}`).then(res => {
            if (res.data.length > 0) setMachineHours(res.data[0].total_hours || 0);
        });
    };

    useEffect(() => {
        fetchAll();
        axiosInstance.get(`mqtt_connection/?project_id=${project_id}`).then(res => {
            const data = res.data;
            setMqttConnections(Array.isArray(data) ? data : (data?.results ?? []));
        });
        axiosInstance.get(`api_connection/?project_id=${project_id}`).then(res => {
            const data = res.data;
            setApiConnections(Array.isArray(data) ? data : (data?.results ?? []));
        });
        axiosInstance.get(`spare_part/?project_id=${project_id}`).then(res => {
            setCatalogParts(res.data);
        });
        axiosInstance.get(`machine/?project_id=${project_id}`).then(res => {
            setAllMachines((res.data || []).filter(m => !m.deleted_at));
        });
    }, [machine.id]);

    const parseEmailChips = (raw) => {
        if (!raw) return [];
        return raw.split(/[,\n]+/).map(e => e.trim()).filter(Boolean);
    };

    const addEmailChip = () => {
        const val = emailInput.trim();
        if (val && !emailChips.includes(val)) setEmailChips(prev => [...prev, val]);
        setEmailInput('');
    };

    const openCreate = () => {
        setEditPlan(null);
        setForm({ project: project_id, interval_hours: '', alert_before_hours: 10, ...EMPTY_FORM });
        setSelectedMachines([machine.id]);
        setMachineSearch('');
        setPlanParts([]);
        setOriginalPlanParts([]);
        setNewPlanPart({ name: '', brand: '', part_number: '', quantity: 1, spare_part: '' });
        setEmailChips([]);
        setEmailInput('');
        setShowModal(true);
    };

    const openEdit = (plan) => {
        setEditPlan(plan);
        let detected = detectNotifyType(plan);
        if (detected === 3 && !systemSettings?.sms_enabled) detected = 1;
        if (detected === 4 && !systemSettings?.whatsapp_enabled) detected = 1;
        setForm({
            ...plan,
            send_notification: planHasNotification(plan),
            notify_type: detected,
        });
        setSelectedMachines(plan.machines || []);
        setMachineSearch('');
        setEmailChips(parseEmailChips(plan.notify_email));
        setEmailInput('');
        const parts = plan.recommended_parts || [];
        setPlanParts(parts);
        setOriginalPlanParts(parts);
        setNewPlanPart({ name: '', brand: '', part_number: '', quantity: 1, spare_part: '' });
        setShowModal(true);
    };

    const selectCatalogPlanPart = (partId) => {
        const found = catalogParts.find(p => p.id === parseInt(partId));
        if (found) {
            setNewPlanPart({ name: found.name, brand: found.brand || '', part_number: found.part_number || '', quantity: 1, spare_part: found.id });
        } else {
            setNewPlanPart({ name: '', brand: '', part_number: '', quantity: 1, spare_part: '' });
        }
    };

    const addPlanPart = () => {
        if (!newPlanPart.name.trim()) return;
        setPlanParts(prev => [...prev, { ...newPlanPart }]);
        setNewPlanPart({ name: '', brand: '', part_number: '', quantity: 1, spare_part: '' });
    };

    const removePlanPart = (idx) => setPlanParts(prev => prev.filter((_, i) => i !== idx));

    const openCreatePart = () => {
        setCreatePartForm({ project: project_id, machine_id: machine.id, initial_quantity: 0 });
        setCreatePartImageFile(null);
        setCreatePartDocFiles([]);
        setShowCreatePartModal(true);
    };

    const saveNewPart = () => {
        if (!createPartForm.name?.trim()) return;
        const formData = new FormData();
        Object.entries(createPartForm).forEach(([k, v]) => { if (v !== undefined && v !== null) formData.append(k, v); });
        if (createPartImageFile) formData.append('image', createPartImageFile);
        axiosInstance.post('spare_part/', formData).then(res => {
            const newPart = res.data;
            const docUploads = createPartDocFiles.map(f => {
                const fd = new FormData();
                fd.append('spare_part', newPart.id);
                fd.append('file', f);
                fd.append('name', f.name);
                return axiosInstance.post('spare_part_document/', fd);
            });
            Promise.all(docUploads).then(() => {
                axiosInstance.get(`spare_part/?project_id=${project_id}`).then(r => setCatalogParts(r.data));
                setPlanParts(prev => [...prev, {
                    name: newPart.name, brand: newPart.brand || '', part_number: newPart.part_number || '',
                    quantity: 1, spare_part: newPart.id,
                }]);
                setShowCreatePartModal(false);
            });
        });
    };

    const savePlan = () => {
        const notifyType = form.send_notification ? parseInt(form.notify_type) : 0;
        const payload = {
            machines: selectedMachines,
            project: project_id,
            name: form.name,
            description: form.description || '',
            interval_hours: parseFloat(form.interval_hours),
            alert_before_hours: isNaN(parseFloat(form.alert_before_hours)) ? 10 : parseFloat(form.alert_before_hours),
            notify_email: notifyType === 1 ? emailChips.join(',') : '',
            notify_sms: notifyType === 3 ? (form.notify_sms || '') : (systemSettings?.sms_enabled ? '' : (editPlan?.notify_sms || '')),
            notify_whatsapp: notifyType === 4 ? (form.notify_whatsapp || '') : (systemSettings?.whatsapp_enabled ? '' : (editPlan?.notify_whatsapp || '')),
            notify_mqtt_connection: notifyType === 5 ? (form.notify_mqtt_connection || null) : null,
            notify_mqtt_topic: notifyType === 5 ? (form.notify_mqtt_topic || '') : '',
            notify_mqtt_payload: notifyType === 5 ? (form.notify_mqtt_payload || '') : '',
            notify_api_connection: notifyType === 6 ? (form.notify_api_connection || null) : null,
            notify_api_endpoint: notifyType === 6 ? (form.notify_api_endpoint || '') : '',
            notify_api_method: notifyType === 6 ? (form.notify_api_method || 'POST') : 'POST',
            notify_api_body: notifyType === 6 ? (form.notify_api_body || '') : '',
        };
        const req = editPlan
            ? axiosInstance.put(`maintenance_plan/${editPlan.id}/`, payload)
            : axiosInstance.post('maintenance_plan/', payload);
        req.then(async (res) => {
            const planId = res.data.id;
            // Diff sync: delete removed parts, add new ones, update changed quantities
            const removedParts = originalPlanParts.filter(op => !planParts.find(p => p.id === op.id));
            const newParts = planParts.filter(p => !p.id);
            const updatedParts = planParts.filter(p => p.id && originalPlanParts.find(op => op.id === p.id && parseInt(op.quantity) !== parseInt(p.quantity)));
            for (const p of removedParts) {
                await axiosInstance.delete(`maintenance_plan/${planId}/remove_part/?part_id=${p.id}`);
            }
            for (const p of updatedParts) {
                await axiosInstance.delete(`maintenance_plan/${planId}/remove_part/?part_id=${p.id}`);
                await axiosInstance.post(`maintenance_plan/${planId}/add_part/`, {
                    name: p.name, brand: p.brand, part_number: p.part_number,
                    quantity: parseInt(p.quantity) || 1, spare_part: p.spare_part || null,
                });
            }
            for (const p of newParts) {
                await axiosInstance.post(`maintenance_plan/${planId}/add_part/`, {
                    name: p.name, brand: p.brand, part_number: p.part_number,
                    quantity: parseInt(p.quantity) || 1, spare_part: p.spare_part || null,
                });
            }
            setShowModal(false);
            fetchAll();
        });
    };

    const confirmDelete = () => {
        axiosInstance.delete(`maintenance_plan/${deletePlan.id}/`).then(() => {
            setShowDeleteModal(false);
            fetchAll();
        });
    };

    const openDoMaintenance = (plan) => {
        setRecordPlan(plan);
        setShowRecordModal(true);
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div />
                <button
                    onClick={openCreate}
                    style={{
                        padding: '8px 16px', borderRadius: '8px', border: 'none',
                        background: '#4F46E5', color: '#FFF', fontWeight: 600,
                        fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                    }}
                >
                    <FontAwesomeIcon icon={faPlus} /> {t('maintenance.createPlan')}
                </button>
            </div>

            {plans.length === 0 && (
                <div style={{ textAlign: 'center', padding: '48px', color: '#94A3B8' }}>
                    {t('maintenance.plans.noPlans')}
                </div>
            )}

            {(() => {
                const plansTotalPages = Math.ceil(plans.length / plansPageSize);
                const pagedPlans = plans.slice((plansPage - 1) * plansPageSize, plansPage * plansPageSize);
                return (<>
            <div className="d-flex flex-column gap-3">
                {pagedPlans.map(plan => (
                    <div key={plan.id} style={{
                        background: '#FFF', borderRadius: '12px', border: '1px solid #E2E8F0',
                        padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px',
                    }}>
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                            <div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span style={{ fontWeight: 600, fontSize: '16px', color: '#1E293B' }}>{plan.name}</span>
                                    {(plan.recommended_parts || []).length > 0 && (
                                        <span style={{ padding: '3px 10px', borderRadius: '12px', background: '#F0F9FF', color: '#0369A1', fontSize: '12px', fontWeight: 600 }}>
                                            {t('maintenance.plans.parts', { count: (plan.recommended_parts || []).length })}
                                        </span>
                                    )}
                                </div>
                                {plan.description && <p style={{ color: '#64748B', fontSize: '13px', margin: '4px 0 0' }}>{plan.description}</p>}
                            </div>
                            <div className="d-flex gap-2">
                                <button
                                    onClick={() => openDoMaintenance(plan)}
                                    style={{
                                        padding: '6px 14px', borderRadius: '6px', border: '1px solid #4F46E5',
                                        background: '#EEF2FF', color: '#4F46E5', fontWeight: 600,
                                        fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px',
                                    }}
                                >
                                    <FontAwesomeIcon icon={faWrench} /> {t('maintenance.doMaintenance')}
                                </button>
                                <button onClick={() => openEdit(plan)} style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', padding: '6px' }}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button onClick={() => { setDeletePlan(plan); setShowDeleteModal(true); }} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', padding: '6px' }}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        </div>

                        {/* Per-machine status */}
                        {(plan.machine_status || []).length > 0 ? (
                            <div className="d-flex flex-column gap-2">
                                {plan.machine_status.map(ms => {
                                    const hoursInCycle = ms.total_hours - (ms.last_maintenance_hours || 0);
                                    return (
                                        <div key={ms.machine_id} style={{ background: '#F8FAFC', borderRadius: '8px', padding: '10px 12px' }}>
                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-1">
                                                <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>{ms.machine_name}</span>
                                                <StatusBadge totalHours={ms.total_hours} nextDueHours={ms.next_due_hours} alertBeforeHours={plan.alert_before_hours} />
                                            </div>
                                            <div className="d-flex justify-content-between" style={{ fontSize: '12px', color: '#64748B', marginTop: '4px' }}>
                                                <span>{t('maintenance.plans.every', {hours: plan.interval_hours, inCycle: hoursInCycle > 0 ? hoursInCycle.toFixed(1) : 0})}</span>
                                                <span>{t('maintenance.plans.dueIn', {remaining: Math.max(0, ms.next_due_hours - ms.total_hours).toFixed(1), total: ms.next_due_hours.toFixed(1)})}</span>
                                            </div>
                                            <ProgressBar current={hoursInCycle} target={plan.interval_hours} />
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{ fontSize: '13px', color: '#94A3B8' }}>
                                {t('maintenance.plans.noMachinesAssigned', 'Sin máquinas asignadas')}
                            </div>
                        )}

                        {/* Notification chips */}
                        <div className="d-flex gap-2 flex-wrap" style={{ fontSize: '12px' }}>
                            {plan.notify_email && <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#EFF6FF', color: '#2563EB' }}><FontAwesomeIcon icon={faEnvelope} /> Email</span>}
                            {plan.notify_sms && systemSettings?.sms_enabled && <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#F0FDF4', color: '#16A34A' }}><FontAwesomeIcon icon={faCommentSms} /> SMS</span>}
                            {plan.notify_whatsapp && systemSettings?.whatsapp_enabled && <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#F0FDF4', color: '#16A34A' }}>WhatsApp</span>}
                            {plan.notify_mqtt_connection && <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#F5F3FF', color: '#7C3AED' }}><FontAwesomeIcon icon={faServer} /> MQTT</span>}
                            {plan.notify_api_connection && <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#FFF7ED', color: '#EA580C' }}><FontAwesomeIcon icon={faGlobe} /> API</span>}
                        </div>
                    </div>
                ))}
            </div>
            <PaginationControls
                page={plansPage}
                totalPages={plansTotalPages}
                pageSize={plansPageSize}
                onPageChange={setPlansPage}
                onPageSizeChange={(newSize) => { setPlansPageSize(newSize); setPlansPage(1); }}
                totalCount={plans.length}
            />
            </>);
            })()}

            {/* Create/Edit modal */}
            <Modal show={showModal} setter={setShowModal} name={editPlan ? t('maintenance.editPlan') : t('maintenance.createPlan')} buttonName={t('common.save')} onClick={savePlan}>
                <TextInput form={form} setForm={setForm} name={t('maintenance.plans.name')} internalName="name" />
                <TextInput form={form} setForm={setForm} name={t('maintenance.plans.descriptionOptional')} internalName="description" />
                <TextInput form={form} setForm={setForm} name={t('maintenance.plans.intervalHours')} internalName="interval_hours" />
                <TextInput form={form} setForm={setForm} name={t('maintenance.plans.alertBeforeHours')} internalName="alert_before_hours" />

                {/* Machine multi-selector */}
                <div style={{ marginTop: '12px' }}>
                    <label style={labelStyle}>{t('maintenance.plans.machines', 'Máquinas')}</label>
                    <input
                        type="text"
                        placeholder={t('maintenance.searchMachines', 'Buscar máquina...')}
                        value={machineSearch}
                        onChange={e => setMachineSearch(e.target.value)}
                        style={{ ...inputStyle, marginBottom: '6px' }}
                    />
                    <div style={{ border: '1px solid #E2E8F0', borderRadius: '6px', maxHeight: '160px', overflowY: 'auto' }}>
                        {allMachines
                            .filter(m => m.name.toLowerCase().includes(machineSearch.toLowerCase()))
                            .map(m => (
                                <label key={m.id} className="d-flex align-items-center gap-2"
                                    style={{ padding: '7px 10px', cursor: 'pointer', borderTop: '1px solid #F1F5F9', fontSize: '13px', color: '#334155' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedMachines.includes(m.id)}
                                        onChange={e => {
                                            if (e.target.checked) {
                                                setSelectedMachines(prev => [...prev, m.id]);
                                            } else {
                                                setSelectedMachines(prev => prev.filter(id => id !== m.id));
                                            }
                                        }}
                                    />
                                    {m.name}
                                </label>
                            ))}
                        {allMachines.filter(m => m.name.toLowerCase().includes(machineSearch.toLowerCase())).length === 0 && (
                            <div style={{ padding: '10px', color: '#94A3B8', fontSize: '13px', textAlign: 'center' }}>
                                {t('maintenance.noMachines', 'Sin resultados')}
                            </div>
                        )}
                    </div>
                    {selectedMachines.length > 0 && (
                        <div className="d-flex flex-wrap gap-1" style={{ marginTop: '6px' }}>
                            {allMachines.filter(m => selectedMachines.includes(m.id)).map(m => (
                                <span key={m.id} style={{ padding: '2px 8px', borderRadius: '12px', background: '#EEF2FF', color: '#4F46E5', fontSize: '12px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    {m.name}
                                    <FontAwesomeIcon icon={faTimes} style={{ cursor: 'pointer', fontSize: '10px' }}
                                        onClick={() => setSelectedMachines(prev => prev.filter(id => id !== m.id))} />
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Notifications */}
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #E2E8F0' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '12px' }}>{t('maintenance.plans.notifications')}</div>

                    <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={form.send_notification || false}
                            onChange={e => setForm({ ...form, send_notification: e.target.checked })}
                            style={{ width: '18px', height: '18px' }}
                        />
                        <span style={{ fontSize: '13px', color: '#334155' }}>{t('maintenance.plans.sendNotification')}</span>
                    </label>

                    {form.send_notification && (
                        <>
<label style={labelStyle}>{t('maintenance.plans.notificationType')}</label>
                            <select
                                value={form.notify_type || 1}
                                onChange={e => setForm({ ...form, notify_type: parseInt(e.target.value) })}
                                style={inputStyle}
                            >
                                {visibleNotifyTypes.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>

                            {/* Email */}
                            {parseInt(form.notify_type) === 1 && (
                                <>
                                    <label style={labelStyle}>{t('maintenance.plans.emailAddress')}</label>
                                    {/* Chips */}
                                    {emailChips.length > 0 && (
                                        <div className="d-flex flex-wrap gap-1" style={{ marginBottom: '6px' }}>
                                            {emailChips.map((chip, i) => (
                                                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '20px', background: '#EFF6FF', color: '#2563EB', fontSize: '12px', fontWeight: 500 }}>
                                                    {chip}
                                                    <FontAwesomeIcon
                                                        icon={faTimes}
                                                        style={{ cursor: 'pointer', fontSize: '10px', marginLeft: '2px' }}
                                                        onClick={() => setEmailChips(prev => prev.filter((_, j) => j !== i))}
                                                    />
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="d-flex gap-2">
                                        <input
                                            type="email"
                                            value={emailInput}
                                            onChange={e => setEmailInput(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmailChip(); } }}
                                            onBlur={addEmailChip}
                                            style={{ ...inputStyle, flex: 1 }}
                                            placeholder="recipient@email.com"
                                        />
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '3px' }}>{t('maintenance.plans.emailMultipleHint')}</div>
                                </>
                            )}

                            {/* SMS */}
                            {parseInt(form.notify_type) === 3 && systemSettings?.sms_enabled && (
                                <>
                                    <label style={labelStyle}>{t('maintenance.plans.phoneNumber')}</label>
                                    <PhoneInput
                                        country={'us'}
                                        value={(form.notify_sms || '').replace(/^\+/, '')}
                                        onChange={phone => setForm({ ...form, notify_sms: '+' + phone })}
                                    />
                                </>
                            )}

                            {/* WhatsApp */}
                            {parseInt(form.notify_type) === 4 && systemSettings?.whatsapp_enabled && (
                                <>
                                    <label style={labelStyle}>{t('maintenance.plans.phoneNumber')}</label>
                                    <PhoneInput
                                        country={'us'}
                                        value={(form.notify_whatsapp || '').replace(/^\+/, '')}
                                        onChange={phone => setForm({ ...form, notify_whatsapp: '+' + phone })}
                                    />
                                </>
                            )}

                            {/* MQTT */}
                            {parseInt(form.notify_type) === 5 && (
                                <>
                                    <label style={labelStyle}>{t('maintenance.plans.mqttConnection')}</label>
                                    <select
                                        value={form.notify_mqtt_connection || ''}
                                        onChange={e => setForm({ ...form, notify_mqtt_connection: e.target.value })}
                                        style={inputStyle}
                                    >
                                        <option value="">{t('maintenance.plans.selectConnection')}</option>
                                        {mqttConnections.map(c => <option key={c.id} value={c.id}>{c.name || c.host}</option>)}
                                    </select>
                                    <label style={labelStyle}>{t('maintenance.plans.topic')}</label>
                                    <PlaceholderInput
                                        value={form.notify_mqtt_topic || ''}
                                        onChange={v => setForm({ ...form, notify_mqtt_topic: v })}
                                        style={inputStyle}
                                        placeholder="maintenance/alerts"
                                        placeholders={PLAN_PLACEHOLDERS}
                                    />
                                    <JsonTextarea
                                        label={t('maintenance.plans.payload')}
                                        value={form.notify_mqtt_payload || ''}
                                        onChange={v => setForm({ ...form, notify_mqtt_payload: v })}
                                        placeholder='{"machine":"{machine_name}","plan":"{plan_name}","hours_due":{hours_due}}'
                                        placeholders={PLAN_PLACEHOLDERS}
                                    />
                                </>
                            )}

                            {/* API */}
                            {parseInt(form.notify_type) === 6 && (
                                <>
                                    <label style={labelStyle}>{t('maintenance.plans.apiConnection')}</label>
                                    <select
                                        value={form.notify_api_connection || ''}
                                        onChange={e => setForm({ ...form, notify_api_connection: e.target.value })}
                                        style={inputStyle}
                                    >
                                        <option value="">{t('maintenance.plans.selectConnection')}</option>
                                        {apiConnections.map(c => <option key={c.id} value={c.id}>{c.name || c.url}</option>)}
                                    </select>
                                    <label style={labelStyle}>{t('maintenance.plans.endpointPath')}</label>
                                    <PlaceholderInput
                                        value={form.notify_api_endpoint || ''}
                                        onChange={v => setForm({ ...form, notify_api_endpoint: v })}
                                        style={inputStyle}
                                        placeholder="/webhook/maintenance-alert"
                                        placeholders={PLAN_PLACEHOLDERS}
                                    />
                                    <label style={labelStyle}>{t('maintenance.plans.method')}</label>
                                    <select
                                        value={form.notify_api_method || 'POST'}
                                        onChange={e => setForm({ ...form, notify_api_method: e.target.value })}
                                        style={inputStyle}
                                    >
                                        {['POST', 'PUT', 'GET'].map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                    <JsonTextarea
                                        label={t('maintenance.plans.bodyJson')}
                                        value={form.notify_api_body || ''}
                                        onChange={v => setForm({ ...form, notify_api_body: v })}
                                        placeholder='{"machine":"{machine_name}","plan":"{plan_name}","hours_due":{hours_due}}'
                                        placeholders={PLAN_PLACEHOLDERS}
                                    />
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* Recommended Parts */}
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #E2E8F0' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '10px' }}>{t('maintenance.plans.recommendedParts')}</div>
                    <div style={{ background: '#F8FAFC', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
                        <div style={{ marginBottom: '8px' }} className="d-flex gap-2 align-items-center">
                            <select
                                value={newPlanPart.spare_part}
                                onChange={e => selectCatalogPlanPart(e.target.value)}
                                style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px' }}
                            >
                                <option value="">{t('maintenance.records.selectFromCatalog')}</option>
                                {catalogParts.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}{p.part_number ? ` (${p.part_number})` : ''}</option>
                                ))}
                            </select>
                            <button
                                onClick={openCreatePart}
                                title={t('maintenance.plans.createNewPart')}
                                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #4F46E5', background: '#EEF2FF', color: '#4F46E5', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                                <FontAwesomeIcon icon={faPlus} /> {t('maintenance.plans.createNewPart')}
                            </button>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <input placeholder={t('maintenance.records.partNameRequired')} value={newPlanPart.name} onChange={e => setNewPlanPart({ ...newPlanPart, name: e.target.value })}
                                style={{ flex: 2, minWidth: '120px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px' }} />
                            <input placeholder={t('maintenance.records.brand')} value={newPlanPart.brand} onChange={e => setNewPlanPart({ ...newPlanPart, brand: e.target.value })}
                                style={{ flex: 1, minWidth: '80px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px' }} />
                            <input placeholder={t('maintenance.records.pn')} value={newPlanPart.part_number} onChange={e => setNewPlanPart({ ...newPlanPart, part_number: e.target.value })}
                                style={{ flex: 1, minWidth: '70px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px' }} />
                            <input type="number" min="1" placeholder="Qty" value={newPlanPart.quantity} onChange={e => setNewPlanPart({ ...newPlanPart, quantity: e.target.value })}
                                style={{ width: '60px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', textAlign: 'center' }} />
                            <button onClick={addPlanPart} style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: '#4F46E5', color: '#FFF', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                                <FontAwesomeIcon icon={faPlus} /> {t('common.add')}
                            </button>
                        </div>
                    </div>
                    {planParts.length > 0 && (
                        <div style={{ border: '1px solid #E2E8F0', borderRadius: '6px', overflow: 'hidden' }}>
                            {planParts.map((p, i) => (
                                <div key={i} className="d-flex justify-content-between align-items-center"
                                    style={{ padding: '8px 12px', borderTop: i > 0 ? '1px solid #F1F5F9' : 'none', fontSize: '13px' }}>
                                    <div>
                                        <span style={{ fontWeight: 500, color: '#1E293B' }}>{p.name}</span>
                                        {p.brand && <span style={{ color: '#64748B', marginLeft: '6px' }}>{p.brand}</span>}
                                        {p.part_number && <span style={{ color: '#94A3B8', marginLeft: '6px', fontFamily: 'monospace' }}>{p.part_number}</span>}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <input
                                            type="number" min="1" value={p.quantity}
                                            onChange={e => setPlanParts(prev => prev.map((part, j) => j === i ? { ...part, quantity: e.target.value } : part))}
                                            style={{ width: '55px', padding: '3px 6px', borderRadius: '5px', border: '1px solid #E2E8F0', fontSize: '13px', textAlign: 'center', fontWeight: 600 }}
                                        />
                                        <button onClick={() => removePlanPart(i)} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', padding: '2px' }}>
                                            <FontAwesomeIcon icon={faTrash} style={{ fontSize: '11px' }} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Modal>

            {/* Delete confirm modal */}
            <Modal show={showDeleteModal} setter={setShowDeleteModal} name={t('maintenance.deletePlan')} buttonName={t('common.delete')} onClick={confirmDelete} danger>
                <p>{t('maintenance.plans.confirmDelete', {name: deletePlan?.name})}</p>
            </Modal>

            {/* Record modal */}
            {showRecordModal && (
                <RecordFormModal
                    show={showRecordModal}
                    setter={setShowRecordModal}
                    machine={machine}
                    plan={recordPlan}
                    onSaved={() => { setShowRecordModal(false); fetchAll(); }}
                />
            )}

            {/* Create Part modal (from Recommended Parts section) */}
            <Modal show={showCreatePartModal} setter={setShowCreatePartModal} name={t('maintenance.newPart')} buttonName={t('common.save')} onClick={saveNewPart}>
                <TextInput form={createPartForm} setForm={setCreatePartForm} name={t('maintenance.plans.name')} internalName="name" />
                <TextInput form={createPartForm} setForm={setCreatePartForm} name={t('maintenance.records.brand')} internalName="brand" />
                <TextInput form={createPartForm} setForm={setCreatePartForm} name={t('maintenance.catalog.model')} internalName="model" />
                <TextInput form={createPartForm} setForm={setCreatePartForm} name={t('maintenance.catalog.partNumberPn')} internalName="part_number" />
                <TextInput form={createPartForm} setForm={setCreatePartForm} name={t('maintenance.catalog.descriptionSpecs')} internalName="description" />
                <TextInput form={createPartForm} setForm={setCreatePartForm} name={t('maintenance.catalog.initialQty')} internalName="initial_quantity" />
                {/* Image */}
                <div style={{ marginTop: '8px' }}>
                    <label style={{ fontSize: '13px', color: '#64748B', display: 'block', marginBottom: '6px' }}>{t('maintenance.catalog.partImage')}</label>
                    {createPartImageFile ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', background: '#F8FAFC', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                                <img src={URL.createObjectURL(createPartImageFile)} alt="" style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
                                <span style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#475569' }}>{createPartImageFile.name}</span>
                            </div>
                            <FontAwesomeIcon icon={faTimes} color="#94A3B8" style={{ cursor: 'pointer', flexShrink: 0, marginLeft: '8px' }} onClick={() => setCreatePartImageFile(null)} />
                        </div>
                    ) : (
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '6px', border: '1px dashed #94A3B8', background: '#F8FAFC', color: '#475569', fontSize: '13px', cursor: 'pointer', userSelect: 'none' }}>
                            <FontAwesomeIcon icon={faImage} size="xs" />
                            {t('maintenance.catalog.partImage')}
                            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setCreatePartImageFile(e.target.files[0])} />
                        </label>
                    )}
                </div>
                {/* Documents */}
                <div style={{ marginTop: '12px' }}>
                    <label style={{ fontSize: '13px', color: '#64748B', display: 'block', marginBottom: '6px' }}>{t('maintenance.catalog.addDocs')}</label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '6px', border: '1px dashed #94A3B8', background: '#F8FAFC', color: '#475569', fontSize: '13px', cursor: 'pointer', userSelect: 'none' }}>
                        <FontAwesomeIcon icon={faPlus} size="xs" />
                        {t('maintenance.catalog.addDocs')}
                        <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.png,.jpg,.jpeg" style={{ display: 'none' }}
                            onChange={e => setCreatePartDocFiles(prev => [...prev, ...Array.from(e.target.files)])} />
                    </label>
                    {createPartDocFiles.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                            {createPartDocFiles.map((file, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', background: '#F8FAFC' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', overflow: 'hidden' }}>
                                        <FontAwesomeIcon icon={file.name.endsWith('.pdf') ? faFilePdf : faFile} color="#64748B" />
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                                    </div>
                                    <FontAwesomeIcon icon={faTimes} color="#94A3B8" style={{ cursor: 'pointer', flexShrink: 0, marginLeft: '8px' }}
                                        onClick={() => setCreatePartDocFiles(prev => prev.filter((_, j) => j !== i))} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
}
